import React from "react";

import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import BuildIcon from "@mui/icons-material/Build";
import CloseIcon from "@mui/icons-material/Close";
import ErrorIcon from "@mui/icons-material/Error";
import ErrorOutlineIcon from "@mui/icons-material/ErrorOutline";
import OpenInNewIcon from "@mui/icons-material/OpenInNew";
import ReplayIcon from "@mui/icons-material/Replay";
import {
  Box,
  Button,
  Chip,
  CircularProgress,
  IconButton,
  LinearProgress,
  Stack,
  Tooltip,
  Typography,
  alpha,
  useTheme,
} from "@mui/material";

import type { BuildLogJob } from "src/domains/application/module/api/IApplicationApi";

import { ElapsedTimer } from "./ElapsedTimer";
import { STEP_ICON } from "./job-detail-constants";
import { STATUS_CHIP_COLOR } from "./job-detail-helpers";

export interface JobDetailHeaderProps {
  activeSvc: string | null;
  onServiceSelect?: (service: string | null) => void;
  step: string;
  title: string;
  displayStatus: string;
  startedAt?: string | null;
  isStale: boolean;
  displayBorderColor: string;
  onClose: () => void;
  isGroupNode: boolean;
  isInProgress: boolean;
  buildFailed?: boolean;
  retryLoading?: boolean;
  onRetryBuild?: () => void;
  forceFailLoading?: boolean;
  onForceFail?: () => void;
  buildLogJobs?: BuildLogJob[];
}

export function JobDetailHeader({
  activeSvc,
  onServiceSelect,
  step,
  title,
  displayStatus,
  startedAt,
  isStale,
  displayBorderColor,
  onClose,
  isGroupNode,
  isInProgress,
  buildFailed,
  retryLoading,
  onRetryBuild,
  forceFailLoading,
  onForceFail,
  buildLogJobs,
}: JobDetailHeaderProps): React.ReactElement {
  const theme = useTheme();

  return (
    <>
      {/* Header */}
      <Stack
        direction="row"
        spacing={1}
        alignItems="center"
        sx={{
          p: 2,
          borderBottom: `1px solid ${alpha(theme.palette.divider, 0.06)}`,
          bgcolor: alpha(displayBorderColor, 0.04),
        }}
      >
        {/* Back button when drilled into a service */}
        {activeSvc && onServiceSelect && (
          <IconButton size="small" onClick={() => onServiceSelect(null)}>
            <ArrowBackIcon fontSize="small" />
          </IconButton>
        )}
        <Box
          sx={{
            width: 32,
            height: 32,
            borderRadius: 1.5,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            bgcolor: displayBorderColor,
            color: "white",
            fontSize: 18,
            flexShrink: 0,
          }}
        >
          {STEP_ICON[step] ?? <BuildIcon />}
        </Box>
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Typography variant="subtitle2" fontWeight={700} noWrap>
            {title}
          </Typography>
          <Stack direction="row" spacing={0.5} alignItems="center">
            <Chip
              label={displayStatus}
              size="small"
              color={STATUS_CHIP_COLOR[displayStatus] || "default"}
              sx={{ height: 18, fontSize: "0.65rem" }}
            />
            {displayStatus === "in_progress" && startedAt && (
              <ElapsedTimer startedAt={startedAt} />
            )}
            {isStale && !activeSvc && (
              <Chip
                label="Stale"
                size="small"
                color="warning"
                variant="outlined"
                sx={{ height: 16, fontSize: "0.6rem" }}
              />
            )}
          </Stack>
        </Box>
        <IconButton size="small" onClick={onClose}>
          <CloseIcon fontSize="small" />
        </IconButton>
      </Stack>

      {/* Action buttons for build step */}
      {step === "build" && !isGroupNode && (
        <Stack direction="row" spacing={1} sx={{ px: 2, pt: 1 }}>
          {buildFailed && onRetryBuild && (
            <Tooltip title="Re-queue a build job with the latest commit SHA">
              <Button
                size="small"
                variant="outlined"
                color="warning"
                startIcon={
                  retryLoading ? (
                    <CircularProgress size={14} />
                  ) : (
                    <ReplayIcon fontSize="small" />
                  )
                }
                onClick={onRetryBuild}
                disabled={retryLoading}
              >
                Retry Build
              </Button>
            </Tooltip>
          )}
          {isStale && onForceFail && (
            <Tooltip title="Force-fail the stuck build job so you can retry">
              <Button
                size="small"
                variant="outlined"
                color="error"
                startIcon={
                  forceFailLoading ? (
                    <CircularProgress size={14} />
                  ) : (
                    <ErrorIcon fontSize="small" />
                  )
                }
                onClick={onForceFail}
                disabled={forceFailLoading}
              >
                Force Fail
              </Button>
            </Tooltip>
          )}
          {buildLogJobs &&
            buildLogJobs.some((j) => j.htmlUrl) &&
            (() => {
              const firstLogUrl = buildLogJobs.find((j) => j.htmlUrl)!.htmlUrl;
              const failedJob = buildLogJobs.find(
                (j) => j.conclusion === "failure" || j.status === "failed",
              );
              return (
                <>
                  <Button
                    size="small"
                    variant="text"
                    startIcon={<OpenInNewIcon fontSize="small" />}
                    href={firstLogUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    View Logs
                  </Button>
                  {failedJob?.htmlUrl && failedJob.htmlUrl !== firstLogUrl && (
                    <Button
                      size="small"
                      variant="text"
                      color="error"
                      startIcon={<ErrorOutlineIcon fontSize="small" />}
                      href={failedJob.htmlUrl}
                      target="_blank"
                      rel="noopener noreferrer"
                    >
                      View Error
                    </Button>
                  )}
                </>
              );
            })()}
        </Stack>
      )}

      {/* Build action buttons at group level (retry/force-fail) */}
      {step === "build" && isGroupNode && !activeSvc && (
        <Stack direction="row" spacing={1} sx={{ px: 2, pt: 1 }}>
          {buildFailed && onRetryBuild && (
            <Tooltip title="Re-queue a build job with the latest commit SHA">
              <Button
                size="small"
                variant="outlined"
                color="warning"
                startIcon={
                  retryLoading ? (
                    <CircularProgress size={14} />
                  ) : (
                    <ReplayIcon fontSize="small" />
                  )
                }
                onClick={onRetryBuild}
                disabled={retryLoading}
              >
                Retry Build
              </Button>
            </Tooltip>
          )}
          {isStale && onForceFail && (
            <Tooltip title="Force-fail the stuck build job so you can retry">
              <Button
                size="small"
                variant="outlined"
                color="error"
                startIcon={
                  forceFailLoading ? (
                    <CircularProgress size={14} />
                  ) : (
                    <ErrorIcon fontSize="small" />
                  )
                }
                onClick={onForceFail}
                disabled={forceFailLoading}
              >
                Force Fail
              </Button>
            </Tooltip>
          )}
        </Stack>
      )}

      {isInProgress && (
        <Box sx={{ px: 2, pt: 1 }}>
          <LinearProgress sx={{ borderRadius: 0.5, height: 2 }} />
        </Box>
      )}
    </>
  );
}
